/**
 * Testing Tab - Test presence status changes
 */

import { useState } from 'react';
import { IconSend, IconMoodSmile } from '@tabler/icons-react';
import { Stack, Paper, Select, TextInput, Button, Group, Divider, Text, Badge } from '../../../../components/ui';
import { usePresenceManager } from '../../../../hooks/usePresenceManager';
import type { PresenceStatus } from '@actschurch/ccms-sdk';
import { notifications } from '@mantine/notifications';

export function TestingTab() {
  const { updateStatus, isConnected, currentUserPresence } = usePresenceManager();
  const [statusForm, setStatusForm] = useState<string>('Online');
  const [activityMessage, setActivityMessage] = useState('');

  const handleUpdateStatus = async () => {
    try {
      await updateStatus(statusForm as PresenceStatus, activityMessage || undefined);
      notifications.show({
        title: 'Success',
        message: 'Status updated successfully',
        color: 'green',
      });
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error?.message || 'Failed to update status',
        color: 'red',
      });
    }
  };

  return (
    <Stack gap="md">
      {/* Current Status */}
      <Paper p="md" withBorder>
        <Stack gap="sm">
          <Text size="sm" fw={600}>
            Current Status
          </Text>
          <Divider />
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Status
            </Text>
            <Badge color="blue">{currentUserPresence?.status || 'Unknown'}</Badge>
          </Group>
          {currentUserPresence?.activityMessage && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Activity Message
              </Text>
              <Text size="sm">{currentUserPresence.activityMessage}</Text>
            </Group>
          )}
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Platform
            </Text>
            <Badge variant="outline">{currentUserPresence?.platform || 'N/A'}</Badge>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Connection
            </Text>
            <Badge color={isConnected ? 'green' : 'red'}>
              {isConnected ? 'Connected' : 'Disconnected'}
            </Badge>
          </Group>
        </Stack>
      </Paper>

      {/* Test Status Update */}
      <Paper p="md" withBorder>
        <Stack gap="md">
          <Group justify="space-between">
            <Text size="sm" fw={600}>
              Test Status Update
            </Text>
            {!isConnected && (
              <Badge color="orange" size="sm">
                Disconnected
              </Badge>
            )}
          </Group>
          <Divider />

          <Select
            label="Status"
            placeholder="Select status"
            value={statusForm}
            onChange={(value) => setStatusForm(value || 'Online')}
            data={[
              { value: 'Online', label: '🟢 Online' },
              { value: 'Away', label: '🟡 Away' },
              { value: 'Busy', label: '🔴 Busy' },
              { value: 'Offline', label: '⚪ Offline' },
            ]}
          />

          <TextInput
            label="Activity Message (Optional)"
            placeholder="Enter activity message..."
            value={activityMessage}
            onChange={(e) => setActivityMessage(e.currentTarget.value)}
            leftSection={<IconMoodSmile size={16} />}
          />

          <Button
            leftSection={<IconSend size={16} />}
            onClick={handleUpdateStatus}
            disabled={!isConnected}
            fullWidth
          >
            Update Status
          </Button>

          {!isConnected && (
            <Text size="xs" c="dimmed" ta="center">
              Connect to PresenceHub to test status updates
            </Text>
          )}
        </Stack>
      </Paper>

      {/* Instructions */}
      <Paper p="md" withBorder>
        <Stack gap="sm">
          <Text size="sm" fw={600}>
            Testing Instructions
          </Text>
          <Divider />
          <Text size="sm" c="dimmed">
            1. Ensure you are connected to the PresenceHub (check Connection tab)
          </Text>
          <Text size="sm" c="dimmed">
            2. Select a status from the dropdown menu
          </Text>
          <Text size="sm" c="dimmed">
            3. Optionally add an activity message
          </Text>
          <Text size="sm" c="dimmed">
            4. Click "Update Status" to broadcast the change
          </Text>
          <Text size="sm" c="dimmed">
            5. Monitor the Event Log tab to see the status change event
          </Text>
        </Stack>
      </Paper>
    </Stack>
  );
}
